import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Activity, Search as SearchIcon, Trash2, RefreshCw, CheckCircle2, XCircle, ChevronDown, ShieldAlert, Unlock, Lock, Gauge, Download, DatabaseBackup, Play, RotateCcw, Clock, AlertTriangle, Save, MessageSquare, Send, Mail, Bell } from "lucide-react";
import { AdminTabs } from "@/components/AdminTabs";
import { FilterPills } from "@/components/FilterPills";
import { DateRangeFilter } from "@/components/DateRangeFilter";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { AdminSummaryCard } from "@/components/admin/AdminSummaryCard";

const fmt = (s) => (s ? new Date(s).toLocaleString() : "—");
const relTime = (s) => {
  if (!s) return null;
  const diff = Date.now() - new Date(s).getTime();
  if (diff < 0) return "just now";
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min${mins === 1 ? "" : "s"} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? "" : "s"} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
};
const fmtBytes = (n) => {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};
const SOURCE_OPTIONS = [
  { key: "all", label: "All" },
  { key: "server", label: "Server" },
  { key: "client", label: "Client" },
];

const StatCard = ({ label, value, tone = "#1E3F33", testid }) => (
  <div className="rounded-2xl border border-[#1E3F33]/10 bg-white p-5" data-testid={testid}>
    <div className="text-2xl font-heading" style={{ color: tone }}>{value}</div>
    <div className="text-xs uppercase tracking-wider text-[#5C6B64] mt-1">{label}</div>
  </div>
);

export default function AdminMonitoring() {
  const [health, setHealth] = useState(null);
  const [security, setSecurity] = useState(null);
  const [backups, setBackups] = useState(null);
  const [testPhone, setTestPhone] = useState("");
  const [smsSending, setSmsSending] = useState(false);
  const [smsStatus, setSmsStatus] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [pushSending, setPushSending] = useState(false);
  const [backingUp, setBackingUp] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoreText, setRestoreText] = useState("");
  const [retentionDraft, setRetentionDraft] = useState("");
  const [savingRetention, setSavingRetention] = useState(false);
  const [restores, setRestores] = useState([]);
  const [data, setData] = useState({ items: [], total: 0 });
  const [q, setQ] = useState("");
  const [source, setSource] = useState("all");
  const [range, setRange] = useState({ start: "", end: "" });
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);

  const { user } = useAuth();
  const isSuper = !!user?.is_super_admin;

  const loadHealth = async () => {
    try {
      const { data } = await api.get("/admin/monitoring/health");
      setHealth(data);
    } catch (e) {
      /* handled by list load */
    }
  };

  const loadSecurity = async () => {
    try {
      const { data } = await api.get("/admin/monitoring/security");
      setSecurity(data);
    } catch (e) {
      /* non-fatal */
    }
  };

  const loadBackups = async () => {
    try {
      const { data } = await api.get("/admin/backups");
      setBackups(data);
      setRetentionDraft(String(data.retention_days));
    } catch (e) {
      /* non-fatal */
    }
  };

  const loadRestores = async () => {
    try {
      const { data } = await api.get("/admin/backups/restores");
      setRestores(data.items || []);
    } catch (e) {
      /* non-fatal */
    }
  };

  const runBackup = async () => {
    setBackingUp(true);
    try {
      const { data } = await api.post("/admin/backups/run");
      if (data.status === "success") toast.success(`Backup created (${fmtBytes(data.size)})`);
      else toast.error(`Backup failed: ${data.error || "unknown error"}`);
      loadBackups();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBackingUp(false);
    }
  };

  const downloadBackup = async (b) => {
    try {
      const res = await api.get(`/admin/backups/${b.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = b.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const openRestore = (b) => {
    setRestoreText("");
    setRestoreTarget(b);
  };

  const confirmRestore = async () => {
    const b = restoreTarget;
    if (!b || restoreText.trim().toUpperCase() !== "RESTORE") return;
    setRestoringId(b.id);
    try {
      const { data } = await api.post(`/admin/backups/${b.id}/restore`);
      toast.success(`Database restored from snapshot — ${data.detail || "done"}`);
      setRestoreTarget(null);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setRestoringId(null);
    }
  };

  const saveRetention = async () => {
    const days = parseInt(retentionDraft, 10);
    if (!days || days < 1 || days > 3650) {
      toast.error("Enter a retention window between 1 and 3650 days.");
      return;
    }
    setSavingRetention(true);
    try {
      await api.put("/admin/backups/settings", { retention_days: days });
      toast.success(`Retention set to ${days} day${days === 1 ? "" : "s"}`);
      loadBackups();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSavingRetention(false);
    }
  };

  const clearBackupErrors = async () => {
    if (!window.confirm("Dismiss all backup error log entries? This clears the resolved backup failures so the log reads clean.")) return;
    try {
      const { data } = await api.delete("/admin/error-logs", { params: { source: "backup" } });
      toast.success(`Dismissed ${data.deleted} backup error entr${data.deleted === 1 ? "y" : "ies"}`);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = { page_size: 100 };
      if (q.trim()) params.q = q.trim();
      if (source !== "all") params.source = source;
      if (range.start) params.start = range.start;
      if (range.end) params.end = range.end;
      const { data } = await api.get("/admin/error-logs", { params });
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHealth();
    loadSecurity();
    loadBackups();
    loadRestores();
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, range]);

  const refresh = () => { loadHealth(); loadSecurity(); loadBackups(); loadRestores(); load(); };

  const releaseLockouts = async () => {
    if (!window.confirm("Release ALL active login lockouts and rate-limit counters? Legitimate users will be unblocked immediately.")) return;
    try {
      const { data } = await api.delete("/admin/monitoring/lockouts");
      toast.success(`Released ${data.lockouts_cleared} lockout record(s)`);
      loadSecurity();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const disableEmergency = async () => {
    if (!window.confirm("Stand down emergency mode now? This lifts the global login throttle, restores normal lockout thresholds, and unblocks all flagged IPs.")) return;
    try {
      const { data } = await api.post("/admin/monitoring/emergency/disable");
      toast.success(`Emergency mode disabled · ${data.unblocked_ips} IP(s) unblocked`);
      loadSecurity();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const exportSecurity = async () => {
    try {
      const res = await api.get("/admin/monitoring/security/export", {
        params: { start: range.start || undefined, end: range.end || undefined },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `security-report-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Security report downloaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const clearAll = async () => {
    if (!window.confirm("Delete ALL error logs? This can't be undone.")) return;
    try {
      await api.delete("/admin/error-logs");
      toast.success("Error logs cleared");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const pollSmsStatus = async (id, attempt = 0) => {
    try {
      const { data } = await api.get(`/admin/monitoring/notification-status/${id}`);
      if (data.verdict === "delivered" || data.verdict === "failed") {
        setSmsStatus({ verdict: data.verdict });
        return;
      }
      if (attempt >= 9) {
        setSmsStatus({ verdict: data.verdict === "pending" ? "pending_timeout" : data.verdict });
        return;
      }
      setSmsStatus({ verdict: "checking" });
      setTimeout(() => pollSmsStatus(id, attempt + 1), 3000);
    } catch (e) {
      if (attempt >= 9) { setSmsStatus({ verdict: "unknown" }); return; }
      setTimeout(() => pollSmsStatus(id, attempt + 1), 3000);
    }
  };

  const sendTestSms = async () => {
    setSmsSending(true);
    setSmsStatus(null);
    try {
      const { data } = await api.post("/admin/monitoring/test-sms", { phone: testPhone.trim() });
      toast.success(`Test SMS accepted — checking carrier delivery…`);
      if (data.id) { setSmsStatus({ verdict: "checking" }); pollSmsStatus(data.id); }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSmsSending(false);
    }
  };

  const sendTestEmail = async () => {
    setEmailSending(true);
    try {
      await api.post("/admin/monitoring/test-email", { email: testEmail.trim() });
      toast.success(`Test email sent to ${testEmail.trim()} — check your inbox.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setEmailSending(false);
    }
  };

  const sendTestPush = async () => {
    setPushSending(true);
    try {
      const { data } = await api.post("/admin/monitoring/test-push");
      toast.success(data.note || "Test push sent — check your device.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setPushSending(false);
    }
  };

  return (
    <div className="container-x py-12" data-testid="admin-monitoring">
      <AdminTabs />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#CC5A3A] mb-2">Operations</div>
          <h1 className="font-heading text-3xl text-[#1E3F33] font-semibold flex items-center gap-2">
            <Activity className="w-6 h-6 text-[#2D6A4F]" /> Monitoring &amp; error logs
          </h1>
          <p className="text-[#5C6B64] mt-2 max-w-2xl">Live health of the platform and a searchable log of server and client-side errors (kept permanently).</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={refresh} data-testid="monitoring-refresh" className="btn-secondary text-sm py-2 px-3 inline-flex items-center gap-2"><RefreshCw className="w-4 h-4" /> Refresh</button>
          <button onClick={clearAll} data-testid="monitoring-clear" className="text-sm py-2 px-3 inline-flex items-center gap-2 rounded-full border border-[#E63946]/40 text-[#E63946] hover:bg-[#E63946]/10"><Trash2 className="w-4 h-4" /> Clear logs</button>
        </div>
      </div>

      {health && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6" data-testid="monitoring-health">
          <div className="rounded-2xl border border-[#1E3F33]/10 bg-white p-5" data-testid="monitoring-db-status">
            <div className={`text-lg font-heading inline-flex items-center gap-2 ${health.db_ok ? "text-[#2D6A4F]" : "text-[#E63946]"}`}>
              {health.db_ok ? <CheckCircle2 className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
              {health.db_ok ? "Healthy" : "Down"}
            </div>
            <div className="text-xs uppercase tracking-wider text-[#5C6B64] mt-1">Database</div>
          </div>
          <StatCard label="Errors (24h)" value={health.errors_24h} tone={health.errors_24h ? "#CC5A3A" : "#1E3F33"} testid="monitoring-errors-24h" />
          <StatCard label="Server (24h)" value={health.server_24h} testid="monitoring-server-24h" />
          <StatCard label="Client (24h)" value={health.client_24h} testid="monitoring-client-24h" />
        </div>
      )}

      {security && (
        <div className="mt-8" data-testid="security-monitor">
          {security.emergency?.active && (
            <div className="mb-5 rounded-2xl border border-[#E63946]/40 bg-[#E63946]/8 p-5 flex items-start justify-between gap-4 flex-wrap" data-testid="emergency-banner">
              <div className="flex items-start gap-3">
                <ShieldAlert className="w-6 h-6 text-[#E63946] shrink-0 mt-0.5" />
                <div>
                  <div className="font-heading text-lg text-[#E63946]">Emergency protection active</div>
                  <p className="text-sm text-[#5C6B64] mt-1 max-w-2xl">
                    {security.emergency.reason ? `Triggered by ${security.emergency.reason}. ` : ""}
                    Tighter lockouts (3 fails / 60 min), a platform-wide login throttle, and offending-IP blocks are engaged.
                    {security.emergency.expires_at ? ` Auto-expires ${fmt(security.emergency.expires_at)}.` : ""}
                  </p>
                </div>
              </div>
              <button onClick={disableEmergency} data-testid="emergency-disable" className="text-sm py-2 px-4 inline-flex items-center gap-2 rounded-full bg-[#E63946] text-white hover:bg-[#c92f3c]">
                <Unlock className="w-4 h-4" /> Disable emergency mode now
              </button>
            </div>
          )}

          <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
            <h2 className="font-heading text-xl text-[#1E3F33] font-semibold flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-[#CC5A3A]" /> Account security
            </h2>
            <div className="flex items-center gap-2">
              <button
                onClick={exportSecurity}
                data-testid="security-export"
                className="text-sm py-2 px-3 inline-flex items-center gap-2 rounded-full border border-[#1E3F33]/20 text-[#1E3F33] hover:bg-[#1E3F33]/5"
              >
                <Download className="w-4 h-4" /> Export report
              </button>
              <button
                onClick={releaseLockouts}
                data-testid="security-release-lockouts"
                disabled={!security.active_lockouts && !security.rate_limited_ips}
                className="text-sm py-2 px-3 inline-flex items-center gap-2 rounded-full border border-[#2D6A4F]/40 text-[#2D6A4F] hover:bg-[#2D6A4F]/10 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Unlock className="w-4 h-4" /> Release lockouts
              </button>
            </div>
          </div>
          <p className="text-xs text-[#8A9A86] -mt-2 mb-4">Export respects the date range selected below. History is kept permanently.</p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Active lockouts" value={security.active_lockouts} tone={security.active_lockouts ? "#E63946" : "#1E3F33"} testid="security-active-lockouts" />
            <StatCard label="Lockouts (24h)" value={security.lockouts_24h} tone={security.lockouts_24h ? "#CC5A3A" : "#1E3F33"} testid="security-lockouts-24h" />
            <StatCard label="Blocked IPs" value={security.blocked_ips?.length || 0} tone={security.blocked_ips?.length ? "#E63946" : "#1E3F33"} testid="security-blocked-ips" />
            <StatCard label="Rate-limited IPs" value={security.rate_limited_ips} tone={security.rate_limited_ips ? "#CC5A3A" : "#1E3F33"} testid="security-rate-limited" />
          </div>

          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <div className="rounded-xl border border-[#1E3F33]/10 overflow-x-auto" data-testid="security-lockouts-panel">
              <div className="bg-[#F0EFEA] px-4 py-2.5 text-sm font-medium text-[#1E3F33] flex items-center gap-2"><Lock className="w-4 h-4 text-[#E63946]" /> Active lockouts</div>
              {security.lockouts.length === 0 ? (
                <p className="px-4 py-6 text-sm text-[#8A9A86]">No accounts are currently locked. 🎉</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-left text-[#5C6B64]"><tr>
                    <th className="px-4 py-2 font-medium">Username</th><th className="px-4 py-2 font-medium">IP</th><th className="px-4 py-2 font-medium">Locked until</th>
                  </tr></thead>
                  <tbody>
                    {security.lockouts.map((l, i) => (
                      <tr key={i} className="border-t border-[#1E3F33]/8" data-testid={`security-lockout-row-${i}`}>
                        <td className="px-4 py-2 text-[#1E3F33]">{l.username}</td>
                        <td className="px-4 py-2 text-[#5C6B64]">{l.ip}</td>
                        <td className="px-4 py-2 text-[#5C6B64] whitespace-nowrap">{fmt(l.locked_until)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="rounded-xl border border-[#1E3F33]/10 overflow-x-auto" data-testid="security-rate-panel">
              <div className="bg-[#F0EFEA] px-4 py-2.5 text-sm font-medium text-[#1E3F33] flex items-center gap-2"><Gauge className="w-4 h-4 text-[#CC5A3A]" /> Rate-limit activity</div>
              {security.rate_buckets.length === 0 ? (
                <p className="px-4 py-6 text-sm text-[#8A9A86]">No rate-limit activity right now.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-left text-[#5C6B64]"><tr>
                    <th className="px-4 py-2 font-medium">Endpoint</th><th className="px-4 py-2 font-medium">IP</th><th className="px-4 py-2 font-medium">Requests</th>
                  </tr></thead>
                  <tbody>
                    {security.rate_buckets.map((b, i) => (
                      <tr key={i} className="border-t border-[#1E3F33]/8" data-testid={`security-rate-row-${i}`}>
                        <td className="px-4 py-2 text-[#1E3F33] capitalize">{b.bucket}</td>
                        <td className="px-4 py-2 text-[#5C6B64]">{b.ip}</td>
                        <td className="px-4 py-2 text-[#5C6B64]">{b.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {security.blocked_ips?.length > 0 && (
            <div className="rounded-xl border border-[#E63946]/20 overflow-x-auto mt-4" data-testid="security-blocked-panel">
              <div className="bg-[#E63946]/8 px-4 py-2.5 text-sm font-medium text-[#E63946] flex items-center gap-2"><ShieldAlert className="w-4 h-4" /> Blocked IPs (attack response)</div>
              <table className="w-full text-sm">
                <thead className="text-left text-[#5C6B64]"><tr>
                  <th className="px-4 py-2 font-medium">IP</th><th className="px-4 py-2 font-medium">Origin</th><th className="px-4 py-2 font-medium">Network</th><th className="px-4 py-2 font-medium">Blocked until</th>
                </tr></thead>
                <tbody>
                  {security.blocked_ips.map((b, i) => (
                    <tr key={i} className="border-t border-[#1E3F33]/8" data-testid={`security-blocked-row-${i}`}>
                      <td className="px-4 py-2 text-[#1E3F33]">{b.ip}</td>
                      <td className="px-4 py-2 text-[#5C6B64]">
                        {b.geo?.country ? (
                          <span data-testid={`security-blocked-geo-${i}`}>
                            {b.geo.country_code && <span className="inline-block text-[10px] font-semibold bg-[#1E3F33]/8 text-[#1E3F33] rounded px-1.5 py-0.5 mr-1.5">{b.geo.country_code}</span>}
                            {b.geo.city ? `${b.geo.city}, ` : ""}{b.geo.country}
                          </span>
                        ) : <span className="text-[#8A9A86]">Unknown</span>}
                      </td>
                      <td className="px-4 py-2 text-[#5C6B64]">
                        {b.geo?.isp || b.geo?.org ? (
                          <span>{b.geo.isp || b.geo.org}{b.geo.asn ? ` · ${b.geo.asn}` : ""}</span>
                        ) : <span className="text-[#8A9A86]">—</span>}
                      </td>
                      <td className="px-4 py-2 text-[#5C6B64] whitespace-nowrap">{fmt(b.expires_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="mt-8 rounded-2xl border border-[#1E3F33]/10 bg-white p-5" data-testid="data-retention-panel">
        <h2 className="font-heading text-xl text-[#1E3F33] font-semibold flex items-center gap-2 mb-1">
          <DatabaseBackup className="w-5 h-5 text-[#2D6A4F]" /> Data & retention
        </h2>
        <p className="text-xs text-[#8A9A86] mb-4">Control how long backup snapshots are kept. System and admin activity logs are never deleted.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="rounded-xl border border-[#1E3F33]/10 p-4">
            <div className="text-sm font-medium text-[#1E3F33] mb-2">Backup snapshots</div>
            <div className="flex items-center gap-2 flex-wrap" data-testid="backup-retention-control">
              <span className="text-xs text-[#5C6B64]">Keep for</span>
              <input
                data-testid="backup-retention-input"
                type="number" min="1" max="3650"
                value={retentionDraft}
                onChange={(e) => setRetentionDraft(e.target.value)}
                className="w-20 rounded-lg border border-[#1E3F33]/20 px-2.5 py-1.5 text-sm text-[#1E3F33] focus:outline-none focus:ring-2 focus:ring-[#2D6A4F]/30"
                disabled={!backups}
              />
              <span className="text-sm text-[#5C6B64]">days</span>
              <button
                onClick={saveRetention}
                disabled={savingRetention || !backups || retentionDraft === String(backups?.retention_days)}
                data-testid="backup-retention-save"
                className="text-xs py-1.5 px-3 inline-flex items-center gap-1.5 rounded-full border border-[#2D6A4F]/40 text-[#2D6A4F] hover:bg-[#2D6A4F]/10 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Save className="w-3.5 h-3.5" /> {savingRetention ? "Saving…" : "Save"}
              </button>
            </div>
          </div>

          {isSuper && (
            <div className="rounded-xl border border-[#1E3F33]/10 p-4">
              <div className="text-sm font-medium text-[#1E3F33] mb-2">Admin activity log</div>
              <p className="text-xs text-[#5C6B64]" data-testid="activity-retention-control">Kept permanently, along with error logs, security events and scheduled-job history. Only temporary login-attempt, rate-limit and IP-block records expire on their own.</p>
            </div>
          )}
        </div>
      </div>

      <div className="mt-8 rounded-2xl border border-[#1E3F33]/10 bg-white p-5" data-testid="test-sms-card">
        <h2 className="font-heading text-xl text-[#1E3F33] font-semibold flex items-center gap-2 mb-1">
          <MessageSquare className="w-5 h-5 text-[#2D6A4F]" /> Test notifications
        </h2>
        <p className="text-xs text-[#8A9A86] mb-4">Send a test message to any channel to confirm OneSignal delivery is working end-to-end.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="flex items-end gap-2 flex-wrap">
            <div className="flex-1 min-w-[180px]">
              <label htmlFor="test-sms-phone" className="text-xs font-semibold uppercase tracking-wider text-[#5C6B64]">SMS to phone</label>
              <input
                id="test-sms-phone"
                data-testid="test-sms-phone"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="+15551234567"
                className="input-soft mt-1 w-full"
              />
            </div>
            <button
              onClick={sendTestSms}
              disabled={smsSending || testPhone.trim().length < 8}
              data-testid="test-sms-send"
              className="text-sm py-2.5 px-4 inline-flex items-center gap-2 rounded-full bg-[#2D6A4F] text-white hover:bg-[#245741] disabled:opacity-40"
            >
              <Send className="w-4 h-4" /> {smsSending ? "Sending…" : "Send SMS"}
            </button>
            {smsStatus && (
              <div
                data-testid="test-sms-status"
                className={`w-full mt-1 text-xs rounded-lg px-3 py-2 flex items-start gap-2 ${
                  smsStatus.verdict === "delivered" ? "bg-[#2D6A4F]/10 text-[#2D6A4F]"
                  : smsStatus.verdict === "filtered" || smsStatus.verdict === "pending_timeout" ? "bg-[#CC5A3A]/12 text-[#CC5A3A]"
                  : smsStatus.verdict === "failed" ? "bg-[#E63946]/12 text-[#E63946]"
                  : "bg-[#1E3F33]/5 text-[#5C6B64]"
                }`}
              >
                {smsStatus.verdict === "checking" && (<><RefreshCw className="w-3.5 h-3.5 mt-0.5 animate-spin shrink-0" /> Checking carrier delivery…</>)}
                {smsStatus.verdict === "delivered" && (<><CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" /> Delivered to the carrier successfully.</>)}
                {smsStatus.verdict === "filtered" && (<><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> OneSignal accepted it, but the carrier didn't deliver it — your toll-free number likely needs carrier verification.</>)}
                {smsStatus.verdict === "failed" && (<><XCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> The carrier reported a delivery failure for this number.</>)}
                {smsStatus.verdict === "pending_timeout" && (<><Clock className="w-3.5 h-3.5 mt-0.5 shrink-0" /> Still pending at the carrier — check the recipient's phone; no result reported yet.</>)}
                {smsStatus.verdict === "unknown" && (<><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> Couldn't read the delivery status from OneSignal.</>)}
              </div>
            )}
          </div>

          <div className="flex items-end gap-2 flex-wrap">
            <div className="flex-1 min-w-[180px]">
              <label htmlFor="test-email-addr" className="text-xs font-semibold uppercase tracking-wider text-[#5C6B64]">Email to address</label>
              <input
                id="test-email-addr"
                data-testid="test-email-addr"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="you@example.com"
                className="input-soft mt-1 w-full"
              />
            </div>
            <button
              onClick={sendTestEmail}
              disabled={emailSending || !testEmail.includes("@")}
              data-testid="test-email-send"
              className="text-sm py-2.5 px-4 inline-flex items-center gap-2 rounded-full bg-[#2D6A4F] text-white hover:bg-[#245741] disabled:opacity-40"
            >
              <Mail className="w-4 h-4" /> {emailSending ? "Sending…" : "Send email"}
            </button>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-[#1E3F33]/8 flex items-center justify-between gap-2 flex-wrap">
          <span className="text-xs text-[#8A9A86]">Push goes to your own subscribed devices (enable browser notifications first).</span>
          <button
            onClick={sendTestPush}
            disabled={pushSending}
            data-testid="test-push-send"
            className="text-sm py-2 px-4 inline-flex items-center gap-2 rounded-full border border-[#2D6A4F]/40 text-[#2D6A4F] hover:bg-[#2D6A4F]/10 disabled:opacity-40"
          >
            <Bell className="w-4 h-4" /> {pushSending ? "Sending…" : "Send test push to me"}
          </button>
        </div>
      </div>

      {isSuper && <AdminSummaryCard />}

      {backups && (
        <div className="mt-8" data-testid="backups-monitor">
          <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
            <h2 className="font-heading text-xl text-[#1E3F33] font-semibold flex items-center gap-2">
              <DatabaseBackup className="w-5 h-5 text-[#2D6A4F]" /> Data backups
            </h2>
            <div className="flex items-center gap-2 flex-wrap">
              {backups.backup_error_count > 0 && (
                <button onClick={clearBackupErrors} data-testid="backup-clear-errors" className="text-sm py-2 px-3 inline-flex items-center gap-2 rounded-full border border-[#CC5A3A]/40 text-[#CC5A3A] hover:bg-[#CC5A3A]/10">
                  <Trash2 className="w-4 h-4" /> Dismiss backup errors ({backups.backup_error_count})
                </button>
              )}
              <button onClick={runBackup} disabled={backingUp} data-testid="backup-run" className="text-sm py-2 px-3 inline-flex items-center gap-2 rounded-full bg-[#2D6A4F] text-white hover:bg-[#245741] disabled:opacity-50">
                <Play className="w-4 h-4" /> {backingUp ? "Backing up…" : "Run backup now"}
              </button>
            </div>
          </div>

          {(() => {
            const rel = relTime(backups.last_success);
            const stale = !backups.last_success || (Date.now() - new Date(backups.last_success).getTime()) > 26 * 3600 * 1000;
            return (
              <div
                data-testid="backup-health-card"
                className={`rounded-2xl border p-5 mb-4 flex items-center gap-4 ${stale ? "border-[#CC5A3A]/30 bg-[#CC5A3A]/8" : "border-[#2D6A4F]/30 bg-[#2D6A4F]/8"}`}
              >
                <div className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${stale ? "bg-[#CC5A3A]/15 text-[#CC5A3A]" : "bg-[#2D6A4F]/15 text-[#2D6A4F]"}`}>
                  {stale ? <AlertTriangle className="w-5 h-5" /> : <CheckCircle2 className="w-5 h-5" />}
                </div>
                <div>
                  <div className={`font-heading text-lg ${stale ? "text-[#CC5A3A]" : "text-[#2D6A4F]"}`} data-testid="backup-health-status">
                    {backups.last_success ? (stale ? "Backup overdue" : "Backups healthy") : "No backup yet"}
                  </div>
                  <div className="text-sm text-[#5C6B64] inline-flex items-center gap-1.5 mt-0.5" data-testid="backup-health-last">
                    <Clock className="w-3.5 h-3.5" />
                    {rel ? `Last backup ${rel}` : "Run your first snapshot to protect your data"}
                    {backups.last_success ? ` · ${fmt(backups.last_success)}` : ""}
                  </div>
                </div>
              </div>
            );
          })()}

          {backups.items.length === 0 && (
            <div data-testid="backup-now-banner" className="rounded-2xl border border-[#2D6A4F]/30 bg-[#2D6A4F]/8 p-5 mb-4 flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-3">
                <DatabaseBackup className="w-6 h-6 text-[#2D6A4F] shrink-0 mt-0.5" />
                <div>
                  <div className="font-heading text-lg text-[#1E3F33]">Protect your data — run your first backup</div>
                  <p className="text-sm text-[#5C6B64] mt-0.5 max-w-2xl">No snapshots exist yet. Create one now so you always have a restore point, then nightly backups run automatically.</p>
                </div>
              </div>
              <button onClick={runBackup} disabled={backingUp} data-testid="backup-now-banner-btn" className="text-sm py-2.5 px-4 inline-flex items-center gap-2 rounded-full bg-[#2D6A4F] text-white hover:bg-[#245741] disabled:opacity-50 shrink-0">
                <Play className="w-4 h-4" /> {backingUp ? "Backing up…" : "Run first backup"}
              </button>
            </div>
          )}

          <p className="text-xs text-[#8A9A86] mb-4">
            Automatic nightly database snapshots, uploaded to secure object storage. {backups.active_count} active · kept {backups.retention_days} days.
          </p>
          <div className="overflow-x-auto rounded-xl border border-[#1E3F33]/10">
            {backups.items.length === 0 ? (
              <p className="px-4 py-6 text-sm text-[#8A9A86]">No backups yet. The first nightly snapshot runs automatically, or trigger one now.</p>
            ) : (
              <table className="w-full text-sm" data-testid="backups-table">
                <thead className="bg-[#F0EFEA] text-left text-[#1E3F33]"><tr>
                  <th className="px-4 py-2.5 font-medium">When</th>
                  <th className="px-4 py-2.5 font-medium">File</th>
                  <th className="px-4 py-2.5 font-medium">Size</th>
                  <th className="px-4 py-2.5 font-medium">Trigger</th>
                  <th className="px-4 py-2.5 font-medium">Status</th>
                  <th className="px-4 py-2.5"></th>
                </tr></thead>
                <tbody>
                  {backups.items.map((b) => (
                    <tr key={b.id} className="border-t border-[#1E3F33]/8" data-testid={`backup-row-${b.id}`}>
                      <td className="px-4 py-2.5 text-[#5C6B64] whitespace-nowrap">{fmt(b.created_at)}</td>
                      <td className="px-4 py-2.5 text-[#1E3F33] max-w-[280px] truncate">{b.filename}</td>
                      <td className="px-4 py-2.5 text-[#5C6B64]">{fmtBytes(b.size)}</td>
                      <td className="px-4 py-2.5 text-[#5C6B64] capitalize">{b.trigger || "—"}</td>
                      <td className="px-4 py-2.5">
                        {b.status === "success" ? (
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${b.expired ? "bg-[#8A9A86]/15 text-[#5C6B64]" : "bg-[#2D6A4F]/12 text-[#2D6A4F]"}`}>{b.expired ? "Expired" : "Active"}</span>
                        ) : (
                          <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-[#E63946]/12 text-[#E63946]" title={b.error || ""}>Failed</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        {b.status === "success" && !b.expired && (
                          <div className="inline-flex items-center gap-3 justify-end">
                            <button onClick={() => downloadBackup(b)} data-testid={`backup-download-${b.id}`} className="text-xs inline-flex items-center gap-1.5 text-[#2D6A4F] hover:underline">
                              <Download className="w-3.5 h-3.5" /> Download
                            </button>
                            <button onClick={() => openRestore(b)} disabled={restoringId === b.id} data-testid={`backup-restore-${b.id}`} className="text-xs inline-flex items-center gap-1.5 text-[#CC5A3A] hover:underline disabled:opacity-50">
                              <RotateCcw className="w-3.5 h-3.5" /> {restoringId === b.id ? "Restoring…" : "Restore"}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {restores.length > 0 && (
            <div className="mt-6" data-testid="restore-history">
              <h3 className="font-heading text-base text-[#1E3F33] font-semibold flex items-center gap-2 mb-2">
                <RotateCcw className="w-4 h-4 text-[#CC5A3A]" /> Restore history
              </h3>
              <p className="text-xs text-[#8A9A86] mb-3">A record of every database restore for accountability.</p>
              <div className="overflow-x-auto rounded-xl border border-[#1E3F33]/10">
                <table className="w-full text-sm" data-testid="restore-history-table">
                  <thead className="bg-[#F0EFEA] text-left text-[#1E3F33]"><tr>
                    <th className="px-4 py-2.5 font-medium">When</th>
                    <th className="px-4 py-2.5 font-medium">By</th>
                    <th className="px-4 py-2.5 font-medium">Snapshot</th>
                    <th className="px-4 py-2.5 font-medium">Result</th>
                  </tr></thead>
                  <tbody>
                    {restores.map((r) => (
                      <tr key={r.id} className="border-t border-[#1E3F33]/8" data-testid={`restore-history-row-${r.id}`}>
                        <td className="px-4 py-2.5 text-[#5C6B64] whitespace-nowrap">{fmt(r.created_at)}</td>
                        <td className="px-4 py-2.5 text-[#1E3F33]">{r.actor || "—"}</td>
                        <td className="px-4 py-2.5 text-[#5C6B64] max-w-[280px] truncate" title={r.filename}>{r.filename}</td>
                        <td className="px-4 py-2.5">
                          {r.status === "success" ? (
                            <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-[#2D6A4F]/12 text-[#2D6A4F]">Success</span>
                          ) : (
                            <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-[#E63946]/12 text-[#E63946]" title={r.detail || ""}>Failed</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="mt-8 space-y-4">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex items-center gap-2 max-w-md">
          <input data-testid="monitoring-search" className="input-soft" placeholder="Search message, path, or stack…" value={q} onChange={(e) => setQ(e.target.value)} />
          <button type="submit" className="btn-primary py-2.5 px-4 inline-flex items-center gap-2"><SearchIcon className="w-4 h-4" /> Search</button>
        </form>
        <div className="flex flex-col gap-3 rounded-xl border border-[#1E3F33]/10 bg-[#F7F6F2] p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#5C6B64]">Source</span>
            <FilterPills options={SOURCE_OPTIONS} value={source} onChange={setSource} testidPrefix="monitoring-source" />
          </div>
          <DateRangeFilter onChange={(start, end) => setRange({ start, end })} />
        </div>
      </div>

      {loading ? (
        <div className="mt-10 text-[#5C6B64]">Loading…</div>
      ) : data.items.length === 0 ? (
        <div className="mt-10 flex flex-col items-center text-center text-[#8A9A86] py-16 border border-dashed border-[#1E3F33]/15 rounded-2xl">
          <CheckCircle2 className="w-8 h-8 mb-3 text-[#2D6A4F]" />
          <p className="text-sm">No errors logged. All clear! 🎉</p>
        </div>
      ) : (
        <div className="mt-8 overflow-x-auto rounded-xl border border-[#1E3F33]/10">
          <table className="w-full text-sm" data-testid="monitoring-table">
            <thead className="bg-[#F0EFEA] text-[#1E3F33] text-left">
              <tr>
                <th className="px-4 py-2.5 font-medium">When</th>
                <th className="px-4 py-2.5 font-medium">Source</th>
                <th className="px-4 py-2.5 font-medium">Status</th>
                <th className="px-4 py-2.5 font-medium">Path</th>
                <th className="px-4 py-2.5 font-medium">Message</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((e) => (
                <React.Fragment key={e.id}>
                  <tr className="border-t border-[#1E3F33]/8 cursor-pointer hover:bg-[#F7F6F2]" data-testid={`monitoring-row-${e.id}`} onClick={() => setExpanded(expanded === e.id ? null : e.id)}>
                    <td className="px-4 py-2.5 text-[#5C6B64] whitespace-nowrap">{fmt(e.created_at)}</td>
                    <td className="px-4 py-2.5">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${e.source === "server" ? "bg-[#CC5A3A]/12 text-[#CC5A3A]" : "bg-[#2D6A4F]/12 text-[#2D6A4F]"}`}>{e.source}</span>
                    </td>
                    <td className="px-4 py-2.5 text-[#5C6B64]">{e.status || "—"}</td>
                    <td className="px-4 py-2.5 text-[#1E3F33] max-w-[220px] truncate">{e.path || "—"}</td>
                    <td className="px-4 py-2.5 text-[#1E3F33] max-w-[360px] truncate">{e.message}</td>
                    <td className="px-4 py-2.5 text-[#8A9A86]"><ChevronDown className={`w-4 h-4 transition-transform ${expanded === e.id ? "rotate-180" : ""}`} /></td>
                  </tr>
                  {expanded === e.id && (
                    <tr className="border-t border-[#1E3F33]/8 bg-[#FBFAF7]" data-testid={`monitoring-detail-${e.id}`}>
                      <td colSpan={6} className="px-4 py-3">
                        <div className="text-xs text-[#5C6B64] space-y-1">
                          <div><span className="font-semibold text-[#1E3F33]">Method:</span> {e.method || "—"} · <span className="font-semibold text-[#1E3F33]">Duration:</span> {e.duration_ms != null ? `${e.duration_ms}ms` : "—"} · <span className="font-semibold text-[#1E3F33]">User:</span> {e.user_id || "anonymous"}</div>
                          {e.user_agent && <div className="break-all"><span className="font-semibold text-[#1E3F33]">UA:</span> {e.user_agent}</div>}
                          {e.stack && <pre className="mt-2 whitespace-pre-wrap break-all bg-[#1E3F33]/5 rounded-lg p-3 text-[11px] leading-relaxed max-h-64 overflow-auto">{e.stack}</pre>}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data.total > data.items.length && (
        <p className="text-xs text-[#8A9A86] mt-3">Showing latest {data.items.length} of {data.total}. Narrow with search or date range.</p>
      )}

      <Dialog open={!!restoreTarget} onOpenChange={(o) => { if (!o) setRestoreTarget(null); }}>
        <DialogContent data-testid="restore-confirm-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#CC5A3A]">
              <AlertTriangle className="w-5 h-5" /> Restore database — this is destructive
            </DialogTitle>
            <DialogDescription>
              You're about to replace <strong>all current data</strong> with the snapshot
              {restoreTarget ? <> <span className="font-mono text-[#1E3F33] break-all">{restoreTarget.filename}</span></> : ""}.
              Any changes made after this backup will be permanently lost and cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label htmlFor="restore-confirm-input" className="text-sm text-[#5C6B64]">
              Type <span className="font-semibold text-[#CC5A3A]">RESTORE</span> to confirm:
            </label>
            <input
              id="restore-confirm-input"
              data-testid="restore-confirm-input"
              value={restoreText}
              onChange={(e) => setRestoreText(e.target.value)}
              autoComplete="off"
              placeholder="RESTORE"
              className="w-full rounded-lg border border-[#1E3F33]/20 px-3 py-2 text-sm text-[#1E3F33] focus:outline-none focus:ring-2 focus:ring-[#CC5A3A]/30"
            />
          </div>
          <DialogFooter>
            <button
              onClick={() => setRestoreTarget(null)}
              data-testid="restore-cancel"
              className="text-sm py-2 px-4 rounded-full border border-[#1E3F33]/20 text-[#1E3F33] hover:bg-[#1E3F33]/5"
            >
              Cancel
            </button>
            <button
              onClick={confirmRestore}
              disabled={restoreText.trim().toUpperCase() !== "RESTORE" || !!restoringId}
              data-testid="restore-confirm-submit"
              className="text-sm py-2 px-4 inline-flex items-center gap-2 rounded-full bg-[#CC5A3A] text-white hover:bg-[#b34d30] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <RotateCcw className="w-4 h-4" /> {restoringId ? "Restoring…" : "Restore database"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
